// packages/permissions-ui/src/labels.ts
//
// ENG-62 — humanized permission labels/descriptions for the frontend.
//
// `PERMISSION_METADATA` (generated by @clearpath/permissions-codegen from the
// control-plane permissions registry) already carries a deterministic
// `label` field for every known permission key ("View orders", "Assign
// steps", ...). This module is the single place every app imports from to
// turn a raw `PermissionKey`-shaped string into that label (or its longer
// `description`) — so no page has to reach into
// `@clearpath/shared-types/permissions` and roll its own fallback.
//
// Why a fallback humanizer at all, given codegen already covers every row?
//   - The registry evolves faster than a shipped frontend bundle; a brand
//     new permission_key can exist server-side before the next `pnpm
//     codegen` run lands in this app.
//   - Some registry rows (e.g. `molaris.*` control-plane/admin permissions —
//     see db/seed/permissions_molaris_admin.sql) aren't in the seed files
//     @clearpath/shared-types's codegen reads by default, so they're simply
//     absent from PERMISSION_METADATA today.
// Either way, the contract is: NEVER render the raw key as primary text.
// `permissionLabel` always returns something a human can read, even for a
// key it has never seen.

import { PERMISSION_METADATA, type PermissionKey } from '@clearpath/shared-types/permissions';

/**
 * Best-effort humanization of a permission key we have no metadata for.
 * `clearpath.orders.view` -> "orders view" -> "Orders view". Deliberately
 * simple (no verb/noun grammar) — this path only fires for keys codegen
 * hasn't caught up to yet, and "Orders view" beats
 * "clearpath.orders.view" every time for an end user.
 */
function fallbackLabel(key: string): string {
  const segments = key.split('.').slice(1); // drop the product namespace
  const words = (segments.length > 0 ? segments : [key]).join(' ').replace(/_/g, ' ').trim();
  if (!words) return 'this action';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Short, human label for a permission key — e.g. "View orders", "Assign
 * steps", "Manage roles". Safe to call with any string, including one that
 * isn't a real `PermissionKey` (never throws, never returns the raw key).
 */
export function permissionLabel(key: string | null | undefined): string {
  if (!key) return 'this action';
  const meta = PERMISSION_METADATA[key as PermissionKey];
  return meta?.label ?? fallbackLabel(key);
}

/**
 * Longer, sentence-style description for a permission key (registry
 * `description` column) — e.g. "View orders within org-unit scope". Falls
 * back to the same short label as `permissionLabel` when no description is
 * on file, so callers can render one field unconditionally.
 */
export function permissionDescription(key: string | null | undefined): string {
  if (!key) return 'this action';
  const meta = PERMISSION_METADATA[key as PermissionKey];
  return meta?.description ?? meta?.label ?? fallbackLabel(key);
}

/** Fallback area for a permission key the generated registry hasn't caught
 *  up to yet — mirrors packages/permissions-codegen/src/labels.ts's own
 *  UNMAPPED_AREA so both "codegen hasn't seen this resource_type" and
 *  "codegen hasn't seen this key at all" degrade to the same bucket. */
const UNMAPPED_AREA = 'Settings & Config';

/**
 * Plain-language area a permission belongs to for the grouped role/
 * permission-picker UX ("Orders & Cases", "Billing & Pricing", ...) — see
 * @clearpath/permissions-codegen's RESOURCE_TYPE_AREAS, baked into
 * PERMISSION_METADATA at codegen time. Never throws; degrades to a
 * reasonable bucket for a key codegen hasn't caught up to yet.
 */
export function permissionArea(key: string | null | undefined): string {
  if (!key) return UNMAPPED_AREA;
  const meta = PERMISSION_METADATA[key as PermissionKey];
  return meta?.area ?? UNMAPPED_AREA;
}

/** True when `key` is a permission the generated registry actually knows about. */
export function isKnownPermissionKey(key: string | null | undefined): key is PermissionKey {
  return !!key && Object.prototype.hasOwnProperty.call(PERMISSION_METADATA, key);
}
